import { Link } from "react-router-dom";
import { DO_OR_DUMP_UPSELL_BUSINESS_PUSH, DO_OR_DUMP_UPSELL_NICHE } from "@/lib/do-or-dump-upsells";

/** Static layout mirror of `/account/profile` for `/demo/account/profile`. */
export function DemoProfilePreview() {
  const sampleEmail = "you@example.com";
  return (
    <div>
      <p className="mb-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
        <strong>Demo preview.</strong> This is what signed-in users see at{" "}
        <Link to="/account/profile" className="font-semibold text-violet-800 underline-offset-2 hover:underline">
          /account/profile
        </Link>
        . Fields are read-only here; saving requires sign-in.
      </p>

      <h1 className="text-2xl font-black tracking-tight text-zinc-900">My profile</h1>
      <p className="mt-2 text-sm text-zinc-600">
        Your email comes from sign-in. Add optional details so we know how to address you and reach you for fulfillments.
      </p>

      <div className="mt-8 space-y-10">
        <div className="space-y-5 rounded-2xl border border-zinc-200 bg-white p-6 shadow-sm">
          <h2 className="text-sm font-bold uppercase tracking-wide text-zinc-500">Contact & details</h2>

          <div className="space-y-1.5">
            <label className="text-sm font-semibold text-zinc-800">Email</label>
            <input
              readOnly
              value={sampleEmail}
              className="w-full cursor-not-allowed rounded-xl border border-zinc-200 bg-zinc-50 px-3 py-2.5 text-sm text-zinc-600"
            />
            <p className="text-xs text-zinc-500">
              Tied to your account email. To change it, contact support.
            </p>
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-semibold text-zinc-800">Name / how we should address you</label>
            <input
              readOnly
              value="Jordan Kim"
              className="w-full cursor-not-allowed rounded-xl border border-zinc-200 bg-zinc-50 px-3 py-2.5 text-sm text-zinc-700"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-semibold text-zinc-800">Phone</label>
            <input
              readOnly
              value="(415) 555-0192"
              className="w-full cursor-not-allowed rounded-xl border border-zinc-200 bg-zinc-50 px-3 py-2.5 text-sm text-zinc-700"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-semibold text-zinc-800">Store / brand / company</label>
            <input
              readOnly
              value="Northwind Ceramics"
              className="w-full cursor-not-allowed rounded-xl border border-zinc-200 bg-zinc-50 px-3 py-2.5 text-sm text-zinc-700"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-semibold text-zinc-800">Anything else we should know</label>
            <textarea
              readOnly
              rows={4}
              value="Shippable mugs — mostly DTC, testing one wholesale account."
              className="w-full cursor-not-allowed resize-none rounded-xl border border-zinc-200 bg-zinc-50 px-3 py-2.5 text-sm text-zinc-700"
            />
          </div>

          <button
            type="button"
            disabled
            className="min-h-11 cursor-not-allowed rounded-xl bg-zinc-200 px-5 text-sm font-bold text-zinc-500"
          >
            Save profile (sign in to use)
          </button>
        </div>

        <div className="space-y-5 rounded-2xl border border-zinc-200 bg-white p-6 shadow-sm">
          <h2 className="text-sm font-bold uppercase tracking-wide text-zinc-500">Password</h2>
          <p className="text-xs text-zinc-600">
            Update the password you use to sign in.
          </p>

          <div className="space-y-1.5">
            <label className="text-sm font-semibold text-zinc-800">New password</label>
            <input
              readOnly
              type="password"
              value="••••••••"
              className="w-full cursor-not-allowed rounded-xl border border-zinc-200 bg-zinc-50 px-3 py-2.5 text-sm text-zinc-500"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-semibold text-zinc-800">Confirm new password</label>
            <input
              readOnly
              type="password"
              value="••••••••"
              className="w-full cursor-not-allowed rounded-xl border border-zinc-200 bg-zinc-50 px-3 py-2.5 text-sm text-zinc-500"
            />
          </div>

          <button
            type="button"
            disabled
            className="min-h-11 cursor-not-allowed rounded-xl border border-zinc-300 bg-zinc-100 px-5 text-sm font-bold text-zinc-500"
          >
            Update password (sign in to use)
          </button>
        </div>
      </div>
    </div>
  );
}

function formatMoney(cents: number, currency: string) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(cents / 100);
}

function statusLabel(status: string): string {
  switch (status) {
    case "paid":
      return "Paid";
    case "processing":
      return "Processing";
    case "fulfilled":
      return "Delivered";
    case "refunded":
      return "Refunded";
    default:
      return status;
  }
}

type MockOrder = {
  id: string;
  created_at: string;
  amount_total_cents: number;
  currency: string;
  status: string;
  line_summary: string;
  fulfillment_note?: string | null;
  delivery_url?: string | null;
  customer_email: string;
};

const DEMO_ORDERS: MockOrder[] = [
  {
    id: "demo-1",
    created_at: new Date(Date.now() - 1000 * 60 * 60 * 24 * 2).toISOString(),
    amount_total_cents: DO_OR_DUMP_UPSELL_NICHE.unitAmountCents + DO_OR_DUMP_UPSELL_BUSINESS_PUSH.unitAmountCents,
    currency: "usd",
    status: "paid",
    line_summary: `${DO_OR_DUMP_UPSELL_NICHE.label} · ${DO_OR_DUMP_UPSELL_BUSINESS_PUSH.label}`,
    customer_email: "you@example.com",
  },
  {
    id: "demo-2",
    created_at: new Date(Date.now() - 1000 * 60 * 60 * 72).toISOString(),
    amount_total_cents: DO_OR_DUMP_UPSELL_BUSINESS_PUSH.unitAmountCents,
    currency: "usd",
    status: "processing",
    line_summary: DO_OR_DUMP_UPSELL_BUSINESS_PUSH.label,
    customer_email: "you@example.com",
  },
  {
    id: "demo-3",
    created_at: new Date(Date.now() - 1000 * 60 * 60 * 24 * 14).toISOString(),
    amount_total_cents: DO_OR_DUMP_UPSELL_NICHE.unitAmountCents,
    currency: "usd",
    status: "fulfilled",
    line_summary: DO_OR_DUMP_UPSELL_NICHE.label,
    fulfillment_note: "Audit attached — reply with follow-ups anytime.",
    delivery_url: "https://example.com/demo-delivery",
    customer_email: "you@example.com",
  },
];

/** Static layout mirror of `/account/orders` for `/demo/account/orders`. */
export function DemoOrdersPreview() {
  const orders = DEMO_ORDERS;

  return (
    <div>
      <p className="mb-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
        <strong>Demo preview.</strong> Sample orders — your real purchases appear at{" "}
        <Link to="/account/orders" className="font-semibold text-violet-800 underline-offset-2 hover:underline">
          /account/orders
        </Link>{" "}
        after checkout and the Stripe webhook.
      </p>

      <h1 className="text-2xl font-black tracking-tight text-zinc-900">My orders</h1>
      <p className="mt-2 text-sm text-zinc-600">
        Purchases from your Do or Dump add-ons appear here after Stripe confirms payment (webhook). Digital delivery links show when we mark an order{" "}
        <span className="font-semibold text-zinc-800">Delivered</span>.
      </p>

      <ul className="mt-8 space-y-4">
        {orders.map((o) => (
          <li key={o.id} className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
                  {new Date(o.created_at).toLocaleString()}
                </p>
                <p className="mt-1 font-bold text-zinc-900">{o.line_summary || "Do or Dump add-on"}</p>
                <p className="mt-1 text-xs text-zinc-500">{o.customer_email}</p>
              </div>
              <div className="text-right">
                <p className="text-lg font-black tabular-nums text-zinc-900">{formatMoney(o.amount_total_cents, o.currency)}</p>
                <span
                  className={`mt-1 inline-block rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide ${
                    o.status === "fulfilled"
                      ? "bg-emerald-100 text-emerald-900"
                      : o.status === "processing"
                        ? "bg-amber-100 text-amber-900"
                        : o.status === "refunded"
                          ? "bg-zinc-200 text-zinc-800"
                          : "bg-violet-100 text-violet-900"
                  }`}
                >
                  {statusLabel(o.status)}
                </span>
              </div>
            </div>

            {o.fulfillment_note ? (
              <p className="mt-3 border-t border-zinc-100 pt-3 text-sm text-zinc-700">{o.fulfillment_note}</p>
            ) : null}

            {o.delivery_url ? (
              <span className="mt-3 inline-flex text-sm font-bold text-zinc-400">
                Open delivery link (demo — disabled)
              </span>
            ) : o.status === "fulfilled" ? null : (
              <p className="mt-3 text-xs text-zinc-500">
                We&apos;ll email you when your purchase is ready, and any download link will appear here.
              </p>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
